<template>
	<v-container fluid class="">
		<h2>Welcome to IRIS</h2>
		<p>
			The IRIS Inventory Platform is a proprietary asset management
			framework built specifically for the distributed inventory, data
			normalization, and asset reconciliation that constitute asset
			inventory best practices.
		</p>
		<p>
			The IRIS asset repository can be the nexus of your worldwide data
			infrastructure. All data collected around the world by your asset
			management staff loads into IRIS via internet load, handheld
			scanners, and direct database loads, to be scrutinized, scrubbed,
			and normalized by your inventory team. Inventory data can also be
			“pushed” from IRIS back out to the field teams for real-time
			corrections, exceptions, and updates.
		</p>
		<p>
			IRIS offers this power and functionality as an on-premises
			installation and/or a cloud-based platform, allowing you to manage
			your inventory and asset data in your own private and secure data
			instances that suit your particular needs.
		</p>
		<div style="overflow-y: hidden">
			<v-card
				v-for="(item, index) in computedItems"
				:key="index"
				class="mx-2 mt-4 mb-0 cardMenu"
			>
				<v-card-title style="justify-content: center">
					<v-icon color="primary" class="mr-2">{{
						item.icon
					}}</v-icon>
					{{ item.title }}
				</v-card-title>
				<v-list>
					<v-list-item-group style="overflow-y: scroll">
						<v-list-item
							v-for="(child, index) in item.children"
							:key="`group-${index}`"
							@click="redirect(item.group, child.to)"
							color="primary"
							style="justify-content: center"
						>
							<v-list-item-icon class="mr-0">
								<v-icon
									color="primary"
									v-text="child.icon"
								></v-icon>
							</v-list-item-icon>
							<v-list-item-content :class="` primary--text`">
								<v-list-item-title>
									<u>{{ child.title }}</u></v-list-item-title
								>
							</v-list-item-content>
						</v-list-item>
					</v-list-item-group>
				</v-list>
			</v-card>
			<v-card
				v-for="(item, index) in adminMenuItems"
				:key="`item-${index}`"
				class="mx-2 mt-4 mb-1 cardMenu"
			>
				<v-card-title style="justify-content: center">
					<v-icon color="primary" class="mr-2">{{
						item.icon
					}}</v-icon>
					{{ item.title }}
				</v-card-title>
				<v-list>
					<v-list-item-group>
						<v-list-item @click="redirect(item.to)">
							<v-list-item-icon class="mr-0">
								<v-icon
									color="primary"
									v-text="item.icon"
								></v-icon>
							</v-list-item-icon>
							<v-list-item-content :class="` primary--text`">
								<v-list-item-title
									><u
										>{{ item.title }} Management</u
									></v-list-item-title
								>
							</v-list-item-content>
						</v-list-item>
					</v-list-item-group>
				</v-list>
			</v-card>
		</div>
	</v-container>
</template>
<script>
import { mapGetters } from 'vuex';
import {
	ProjectSettingsGetters,
	ClientManagementGetters,
	ProjectManagementGetters,
	GridsGetters,
	InventoryFormGetters,
	ReportGetters,
	ActivityGetters,
} from '../store/getters';
import { InventoryFormTypes } from '../enums/inventoryFormTypes';
import permissionsMixin from '../mixins/menuPermissionMixin';
export default {
	name: 'Homepage',
	mixins: [permissionsMixin],
	data() {
		return {
			showSelectClientModal: false,
			showSelectProjectModal: false,
			items: [],
		};
	},
	computed: {
		...mapGetters({
			clientsManagement: [ClientManagementGetters.INITIALIZED],
			projectManagement: [ProjectManagementGetters.INITIALIZED],

			projectSettings: [ProjectSettingsGetters.INITIALIZED],

			gridsModule: [GridsGetters.INITIALIZED],
			gridsMenuEntities: [GridsGetters.GET_MENU_ENTITIES],

			inventoryFormsModule: [InventoryFormGetters.INITIALIZED],
			inventoryFormsMenuEntities: [InventoryFormGetters.MENU_FORMS],

			reports: [ReportGetters.MENU_ITEMS_REPORTS_PREVIEWS],
			activities: [ActivityGetters.ACTIVITIES],
		}),
		computedItems() {
			let items = [];

			if (this.gridsModuleMenu && this.canViewGrids) {
				items = this.apppendMenuItems(items, this.gridsModuleMenu, 1);
			}

			if (this.inventoryModuleMenu && this.canViewInventory) {
				items = this.apppendMenuItems(
					items,
					this.inventoryModuleMenu,
					2
				);
			}
			if (this.activitiesMenu && this.canViewActivity) {
				items = this.apppendMenuItems(items, this.activitiesMenu, 0);
			}
			if (this.reportsMenu && this.canViewReports) {
				items = this.apppendMenuItems(items, this.reportsMenu, 3);
			}
			if (this.toolsMenu && this.canViewTools) {
				items = this.apppendMenuItems(items, this.toolsMenu, 4);
			}

			if (this.settingsMenu && this.canViewSettings) {
				items = this.apppendMenuItems(items, this.settingsMenu, 5);
			}

			return items;
		},
		reportsMenu() {
			let children = [];
			this.reports.forEach((item) => {
				children = this.apppendMenuItems(
					children,
					{
						icon: 'mdi-chart-box-outline',
						title: item.ReportName,
						to: `${item.ReportId}/gridView/${item.GridViewId}`,
					},
					0
				);
			});
			if (children.length > 0) {
				let item = {
					group: '/reports',
					icon: 'mdi-chart-box-plus-outline',
					title: 'Reports',
					children: children.sort((a, b) =>
						a.title > b.title ? 1 : -1
					),
				};
				return item;
			}
			return null;
		},
		gridsModuleMenu() {
			if (this.gridsModule) {
				let children = [];
				this.gridsMenuEntities.forEach((entity) => {
					switch (entity.Name) {
						case 'Asset':
							children = this.apppendMenuItems(
								children,
								{
									icon: 'mdi-file-multiple',
									title: 'Assets',
									to: entity.Name,
								},
								0
							);
							break;
						case 'Catalog':
							children = this.apppendMenuItems(
								children,
								{
									icon: 'mdi-tag-multiple',
									title: 'Catalogs',
									to: entity.Name,
								},
								1
							);
							break;
						case 'Department':
							children = this.apppendMenuItems(
								children,
								{
									icon: 'mdi-vector-polyline',
									title: 'Departments',
									to: entity.Name,
								},
								2
							);
							break;
						case 'Name':
							children = this.apppendMenuItems(
								children,
								{
									icon: 'mdi-account-plus',
									title: 'End-User Names',
									to: entity.Name,
								},
								3
							);
							break;
						case 'Location':
							children = this.apppendMenuItems(
								children,
								{
									icon: 'mdi-map-marker-multiple',
									title: 'Locations',
									to: entity.Name,
								},
								4
							);
							break;
						default:
							children.push({
								icon: 'mdi-view-grid-outline',
								title: entity.Name,
								to: entity.Name,
							});
							break;
					}
				});
				if (children.length > 0) {
					let item = {
						group: '/grids',
						icon: 'mdi-grid',
						title: 'Datasets',
						children: children.sort((a, b) =>
							a.title > b.title ? 1 : -1
						),
						permissionsNeeded: [],
					};
					return item;
				}
			}
			return null;
		},

		inventoryModuleMenu() {
			if (this.inventoryFormsModule) {
				let children = [];
				this.inventoryFormsMenuEntities.forEach((item) => {
					if (item.Type === InventoryFormTypes.Inventory)
						children = this.apppendMenuItems(
							children,
							{
								icon: 'mdi-file-document-edit',
								title: item.Name,
								to: `${item.Id}/form`,
							},
							0
						);
					else
						children = this.apppendMenuItems(
							children,
							{
								icon: 'mdi-file-document-edit',
								title: item.Name,
								to: `${item.Id}/grid`,
							},
							0
						);
				});
				if (children.length > 0) {
					let item = {
						group: '/inventory',
						icon: 'mdi-clipboard-text-outline',
						title: 'Inventory',
						children: children.sort((a, b) =>
							a.title > b.title ? 1 : -1
						),
					};
					return item;
				}
			}
			return null;
		},
		activitiesMenu() {
			let children = [];
			this.activities.forEach((item) => {
				children = this.apppendMenuItems(
					children,
					{
						icon: 'mdi-calendar-month-outline',
						title: item.Name,
						to: `${item.Id}`,
					},
					0
				);
			});
			if (children.length > 0) {
				let item = {
					group: '/activity',
					icon: 'mdi-calendar-month-outline',
					title: 'Activities',
					children: children.sort((a, b) =>
						a.title > b.title ? 1 : -1
					),
				};
				return item;
			}
			return null;
		},
		toolsMenu() {
			let formBuilder = {
				icon: 'mdi-text-box-plus',
				title: 'Form builder',
				to: 'form-builder',
			};
			let activityBuilder = {
				icon: 'mdi-text-box-plus',
				title: 'Activity builder',
				to: 'activity-builder',
			};
			let reportBuilder = {
				icon: 'mdi-chart-box-plus-outline',
				title: 'Report builder',
				to: 'report-builder',
			};
			let dataImport = {
				icon: 'mdi-database-import',
				title: 'Data import',
				to: 'data-import',
			};
			let userManagement = {
				icon: 'mdi-account-group',
				title: 'User management',
				to: 'user-management',
			};
			let projectClone = {
				icon: 'mdi-content-duplicate',
				title: 'Clone Project',
				to: 'clone-project',
			};
			let children = [];
			if (this.canViewFormBuilder) {
				children = this.apppendMenuItems(children, formBuilder, 0);
			}
			if (this.canViewReportBuilder) {
				children = this.apppendMenuItems(children, reportBuilder, 1);
			}
			if (this.canViewDataImport) {
				children = this.apppendMenuItems(children, dataImport, 2);
			}
			if (this.canViewUserManagement) {
				children = this.apppendMenuItems(children, userManagement, 3);
			}
			if (this.canViewActivityBuilder) {
				children = this.apppendMenuItems(children, activityBuilder, 4);
			}
			if (this.canViewProjectClone) {
				children = this.apppendMenuItems(children, projectClone, 5);
			}
			let item = {
				group: '/tools',
				icon: 'mdi-hammer-wrench',
				title: 'Tools',
				children: children.sort((a, b) => (a.title > b.title ? 1 : -1)),
			};

			return item;
		},

		settingsMenu() {
			if (this.gridsModule || this.projectSettings) {
				let dataField = {
					icon: 'mdi-database-cog',
					title: 'Database tables',
					to: 'data-fields',
					permissionsNeeded: [],
				};
				let referenceList = {
					icon: 'mdi-form-dropdown',
					title: 'Reference Lists',
					to: 'reference-lists',
					permissionsNeeded: [],
				};
				let gridFields = {
					icon: 'mdi-view-grid',
					title: 'Dataset fields',
					to: 'grid-fields',
					permissionsNeeded: [],
				};
				let generalSettings = {
					icon: 'mdi-cogs',
					title: 'General',
					to: 'general-settings',
					permissionsNeeded: [],
				};
				let mergeConditions = {
					icon: 'mdi-call-merge',
					title: 'Merge Conditions',
					to: 'merge-conditions',
					permissionsNeeded: [],
				};
				let connectors = {
					icon: 'mdi-connection',
					title: 'Dataset connectors',
					to: 'connectors',
					permissionsNeeded: [],
				};
				let children = [];
				if (this.projectSettings) {
					if (this.canViewGeneralSettings) {
						children = this.apppendMenuItems(
							children,
							generalSettings,
							0
						);
					}
					if (this.canViewDataFields) {
						children = this.apppendMenuItems(
							children,
							dataField,
							1
						);
					}
					if (this.canViewReferenceLists)
						children = this.apppendMenuItems(
							children,
							referenceList,
							2
						);
					if (this.canViewMergeConditions)
						children = this.apppendMenuItems(
							children,
							mergeConditions,
							3
						);
				}

				if (this.gridsModule) {
					if (this.canViewGridFields) {
						children = this.apppendMenuItems(
							children,
							gridFields,
							4
						);
					}
				}
				children = this.apppendMenuItems(children, connectors, 5);

				let item = {
					group: '/settings',
					icon: 'mdi-cog',
					title: 'Settings',
					children: children.sort((a, b) =>
						a.title > b.title ? 1 : -1
					),
				};

				return item;
			}

			return null;
		},

		adminMenuItems() {
			let items = [];
			if (this.clientsManagement && this.canViewClients) {
				let item = {
					icon: 'mdi-account-multiple',
					title: 'Clients',
					to: '/clients-management',
				};
				if (items.length === 0) {
					items.push(item);
				} else {
					items.splice(0, 0, item);
				}
			}

			if (this.projectManagement && this.canViewProjectsMenu) {
				let item = {
					icon: 'mdi-file',
					title: 'Projects',
					to: '/projects-management',
				};
				items.push(item);
			}
			return items;
		},
	},

	methods: {
		redirect(group, page) {
			if (group === null) this.$router.push(`/${page}`);
			else if (page === undefined) this.$router.push(`${group}`);
			else this.$router.push(`${group}/${page}`);
		},

		apppendMenuItems(items, menuItem, defaultPosition) {
			if (items.length > defaultPosition) {
				items.splice(defaultPosition, 0, menuItem);
			} else {
				items.push(menuItem);
			}
			return items;
		},
	},
};
</script>
<style scoped>
.cardMenu {
	float: left;
	width: 250px;
	height: 340px;
	overflow-y: scroll;
	overflow-x: hidden;
}
</style>
